import LaunchIcon from "@mui/icons-material/Launch";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { csCZ } from "@mui/x-data-grid/locales";
import { useNotifications } from "@toolpad/core";
import { useEffect, useState } from "react";
import { fetchAvailableYears, fetchFaktury } from "../services/fakturyService";
import { getPdfFaktura } from "../services/pdfService";
import { compareCurrency, formatCurrency } from "../utils/currencyUtils";
import { compareDate, formatDate } from "../utils/dateUtils";
import { handleApiError } from "../utils/errorHandler";

const formatPredmetFakturace = (priznak) => {
  if (typeof priznak === "string" && priznak.toUpperCase() === "S") {
    return "Služby";
  }

  if (typeof priznak === "string" && priznak.toUpperCase() === "V") {
    return "Vodné a stočné";
  }

  return "-";
};

export default function FakturyPage() {
  const currentYear = new Date().getFullYear();
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [faktury, setFaktury] = useState([]);
  const [selectedLimit, setSelectedLimit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // <600 px
  const notifications = useNotifications();

  useEffect(() => {
    async function fetchYears() {
      const result = await fetchAvailableYears();
      if (result.success) {
        const data = result.data;

        if (!data.includes(currentYear)) {
          if (data.length === 0) {
            return;
          }
          setAvailableYears([currentYear, ...data.reverse()]);
        } else {
          setAvailableYears(data.reverse());
        }
      } else {
        setError(handleApiError(result));
      }
    }
    fetchYears();
  }, [currentYear]);

  useEffect(() => {
    async function fetchData() {
      const result = await fetchFaktury(selectedYear, selectedLimit);
      if (result.success) {
        setFaktury(result.data.faktury);
      } else {
        setError(handleApiError(result));
      }
      setLoading(false);
    }
    fetchData();
  }, [selectedYear, selectedLimit]);

  const handleCisloFakturyClick = async (cisloFaktury) => {
    const result = await getPdfFaktura(cisloFaktury);

    if (!result.success) {
      if (result.status === 404) {
        notifications.show(`PDF faktura č. ${cisloFaktury} nebyla nalezena.`, {
          severity: "error",
          autoHideDuration: 5000,
        });
        return;
      }
      const customError = new Error(result.message || "Neznámá chyba");
      customError.status = result.status;
      setError(customError);
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    throw new Response(error.message, { status: error.status });
  }

  // Definice sloupců pro DataGrid
  const columns = [
    {
      field: "cisloFaktury",
      minWidth: 160,
      headerName: "Číslo faktury",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) =>
        params.row ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>{params.row.cisloFaktury}</Typography>
            <LaunchIcon
              color="primary"
              sx={{ cursor: "pointer", marginLeft: "10px" }}
              onClick={() => handleCisloFakturyClick(params.row.cisloFaktury)}
            />
          </Box>
        ) : (
          "-"
        ),
    },
    {
      field: "vystaveni",
      minWidth: 120,
      headerName: "Vystavení",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortComparator: compareDate,
      valueGetter: (value) => (value ? formatDate(value) : "-"),
    },
    {
      field: "priznak",
      minWidth: 145,
      headerName: "Předmět",
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueGetter: (value) => formatPredmetFakturace(value),
    },
    {
      field: "splatnost",
      minWidth: 120,
      headerName: "Splatnost",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortComparator: compareDate,
      valueGetter: (value) => (value ? formatDate(value) : "-"),
    },
    {
      field: "castka",
      minWidth: 120,
      headerName: "Částka",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortComparator: compareCurrency,
      valueGetter: (value) => (value ? formatCurrency(value) : "-"),
    },
    {
      field: "uhrada",
      minWidth: 150,
      headerName: "Datum Úhrady",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortComparator: compareDate,
      valueGetter: (value) => (value ? formatDate(value) : "-"),
    },
    {
      field: "zbyva",
      minWidth: 150,
      headerName: "Zbývá k úhradě",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortComparator: compareCurrency,
      renderCell: (params) =>
        `${params.row?.uhrada ? formatCurrency(0) : formatCurrency(params.row.castka)}`,
    },
    // „Dnů po splatnosti“
    {
      field: "pastDueDays",
      minWidth: 150,
      headerName: "Dnů po splatnosti",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const { splatnost, uhrada, castka } = params.row;
        // pokud zaplaceno, není splatnost nebo částka je 0, vypíšeme „-“
        if (!splatnost || uhrada || !castka || castka === 0) {
          return "-";
        }
        const dueDate = new Date(splatnost);
        const today = new Date();
        dueDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        const diffDays = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));

        let text;
        let highlight;
        if (diffDays > 0) {
          text = `${diffDays}`;
          highlight = "rgb(255, 0, 0)";
        } else {
          text = "-";
          highlight = "transparent";
        }

        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: highlight,
            }}
          >
            {text}
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Faktury
      </Typography>
      <Box sx={{ mb: 2, textAlign: "center" }}>
        {availableYears?.length > 0 && (
          <>
            <FormControl
              sx={{ minWidth: "180px", width: { xs: "100%", sm: "auto" } }}
            >
              <InputLabel id="yearSelector">Zobrazit za rok</InputLabel>
              <Select
                labelId="yearSelector"
                id="yearSelector"
                value={selectedYear}
                label="Zobrazit za rok"
                onChange={(event) => setSelectedYear(event.target.value)}
              >
                <MenuItem
                  value=""
                  sx={{
                    // height: "20px",
                    textAlign: "center",
                    justifyContent: "center",
                  }}
                >
                  -
                </MenuItem>
                {[...availableYears].map((year) => (
                  <MenuItem
                    value={year}
                    key={year}
                    sx={{
                      // height: "20px",
                      textAlign: "center",
                      justifyContent: "center",
                    }}
                  >
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
      </Box>
      {/* Tabulka faktur */}
      {faktury.length > 0 ? (
        <>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
            }}
          >
            {selectedYear ? (
              `Faktury za ${selectedYear}`
            ) : (
              <>
                Posledních
                <FormControl size="small" sx={{ minWidth: 60 }}>
                  <Select
                    value={selectedLimit}
                    onChange={(e) => setSelectedLimit(e.target.value)}
                    sx={{ fontSize: "inherit", ml: 1, mr: 1 }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
          </Typography>
          <Paper sx={{ mt: 2 }}>
            <DataGrid
              rows={faktury}
              columns={columns}
              pageSizeOptions={
                isMobile ? [5] : [5, 10, 25, { value: -1, label: "Vše" }]
              }
              density={isMobile ? "compact" : "standard"}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              rowSelection={false}
              disableRowSelectionOnClick={true}
              autoWidth={false}
              getRowId={(row) => row?.cisloFaktury ?? Math.random()}
              localeText={csCZ.components.MuiDataGrid.defaultProps.localeText}
              sx={(theme) => ({
                ...theme.components?.MuiDataGrid?.styleOverrides?.root,
                "& .MuiDataGrid-virtualScroller": {
                  overflowX: "auto",
                },
              })}
              slotProps={{
                pagination: {
                  labelRowsPerPage: "Řádků na stránku:",
                },
              }}
            />
          </Paper>
        </>
      ) : (
        <>
          {availableYears?.length > 0 ? (
            <Box sx={{ textAlign: "center", my: 4 }}>
              <Typography variant="body1">
                Nemáte žádnou fakturu za tento rok.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", my: 4 }}>
              <Typography variant="body1">Nemáte žádné faktury.</Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
